import { useState, useEffect, useCallback, useRef } from 'react';
import { Trophy, Loader2, ChevronRight, Plus, Minus, X, Users, FileDown, CalendarClock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { getKnockout, generateKnockout, advanceKnockout, updateMatch, getSettings, getPlayers, getMatchGoals, getMatches, getTeams, realignKnockoutDates } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const MatchCard = ({ match, onEdit, showLeg = true, onDownloadReport }) => {
  if (!match) return null;
  const homeTeam = match.home_team?.name || 'TBD';
  const awayTeam = match.away_team?.name || 'TBD';

  return (
    <div
      className={cn(
        "p-3 bg-slate-800/50 rounded-lg border transition-all hover:border-primary/50 relative group",
        match.is_played ? "border-green-500/30" : "border-slate-700"
      )}
      data-testid={`knockout-match-${match.id}`}
    >
      {onDownloadReport && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDownloadReport(match); }}
          className="absolute top-2 right-2 p-1.5 rounded bg-slate-900/60 border border-slate-700 hover:border-primary text-slate-400 hover:text-primary transition-colors"
          title="Scarica report PDF della partita"
          data-testid={`download-match-report-${match.id}`}
        >
          <FileDown className="w-3.5 h-3.5" />
        </button>
      )}
      <div className="cursor-pointer" onClick={() => onEdit(match)}>
        {showLeg && match.leg && (
          <div className="text-xs text-slate-500 mb-2">
            {match.leg === 1 ? 'ANDATA' : 'RITORNO'}
          </div>
        )}
        <div className="flex items-center justify-between pr-7">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate">{homeTeam}</p>
          </div>
          <div className="flex items-center gap-2 px-3 flex-shrink-0">
            {match.is_played ? (
              <>
                <span className="font-numbers text-lg font-bold text-primary">{match.home_score}</span>
                <span className="text-slate-600">-</span>
                <span className="font-numbers text-lg font-bold text-primary">{match.away_score}</span>
              </>
            ) : (
              <span className="text-slate-500 text-sm">vs</span>
            )}
          </div>
          <div className="flex-1 min-w-0 text-right">
            <p className="font-semibold text-sm truncate">{awayTeam}</p>
          </div>
        </div>
        {!match.is_played && (
          <div className="text-xs text-slate-500 mt-2 text-center">
            {new Date(match.match_date).toLocaleDateString('it-IT', {
              weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC'
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const TieDisplay = ({ leg1, leg2 }) => {
  if (!leg1 || !leg2 || !leg1.is_played || !leg2.is_played) return null;
  const team1Id = leg1.home_team_id;
  const team1Total = (leg1.home_score || 0) + (leg2.away_score || 0);
  const team2Total = (leg1.away_score || 0) + (leg2.home_score || 0);
  const team1Away = leg2.away_score || 0;
  const team2Away = leg1.away_score || 0;
  let winnerId = null;
  if (team1Total > team2Total) winnerId = team1Id;
  else if (team2Total > team1Total) winnerId = leg1.away_team_id;
  else if (team1Away > team2Away) winnerId = team1Id;
  else winnerId = leg1.away_team_id;

  return (
    <div className="text-center mt-2 py-2 border-t border-slate-700">
      <span className="text-xs text-slate-400">Totale: </span>
      <span className={cn("font-numbers font-bold mx-1", winnerId === team1Id ? "text-primary" : "text-slate-400")}>{team1Total}</span>
      <span className="text-slate-600">-</span>
      <span className={cn("font-numbers font-bold mx-1", winnerId !== team1Id ? "text-primary" : "text-slate-400")}>{team2Total}</span>
      {team1Total === team2Total && (
        <span className="text-xs text-gold ml-2">(gol in trasferta)</span>
      )}
    </div>
  );
};

// Placeholder card per fasi non ancora generate: mostra label + data + ora
const PlaceholderLeg = ({ legLabel, homeHint, awayHint, date, time }) => (
  <div className="p-3 bg-slate-800/30 rounded-lg border border-dashed border-slate-700">
    <div className="text-xs text-slate-500 mb-2">{legLabel}</div>
    <div className="flex items-center justify-between gap-2">
      <p className="font-semibold text-sm text-slate-400 flex-1 min-w-0 truncate">{homeHint}</p>
      <span className="text-slate-600 text-sm flex-shrink-0">vs</span>
      <p className="font-semibold text-sm text-slate-400 flex-1 min-w-0 truncate text-right">{awayHint}</p>
    </div>
    {(date || time) && (
      <div className="text-xs text-slate-500 mt-2 text-center font-numbers">
        {date && new Date(`${date}T00:00:00Z`).toLocaleDateString('it-IT', {
          weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'
        })}
        {time && <span className="ml-2 text-primary/80">· {time}</span>}
      </div>
    )}
  </div>
);

const KnockoutPlaceholders = ({ settings }) => {
  const schedule = settings?.match_schedule || [];
  const findEntry = (re) => schedule.find(s => re.test((s.label || '').toLowerCase()));
  const dateOf = (e) => e ? e.date : null;
  const timeAt = (e, idx) => (e && Array.isArray(e.times)) ? e.times[idx] : null;

  const qfAnd1 = findEntry(/^1.*quarto.*andata/);
  const qfRit1 = findEntry(/^1.*quarto.*ritorno/);
  const qfAnd2 = findEntry(/^2.*quarto.*andata/);
  const qfRit2 = findEntry(/^2.*quarto.*ritorno/);
  const sfAnd = findEntry(/semifinal.*andata/);
  const sfRit = findEntry(/semifinal.*ritorno/);
  const finale = findEntry(/^finale$/);

  // Order in which placeholders consume time slots within each schedule entry:
  //   QF1 -> slot0, QF2 -> slot1 (sera "1 Quarto Andata") + idem ritorno
  //   QF3 -> slot0, QF4 -> slot1 (sera "2 Quarto Andata")  + idem ritorno
  //   SF1 -> slot0, SF2 -> slot1 (Andata + Ritorno)
  //   Finale 3/4 -> slot0, Finale 1/2 -> slot1 (sera Finale)
  const quarters = [
    { num: 1, home: '1°C', away: '2°A', andata: { date: dateOf(qfAnd1), time: timeAt(qfAnd1, 0) }, ritorno: { date: dateOf(qfRit1), time: timeAt(qfRit1, 0) } },
    { num: 2, home: '1°A', away: '1ª miglior 3ª', andata: { date: dateOf(qfAnd1), time: timeAt(qfAnd1, 1) }, ritorno: { date: dateOf(qfRit1), time: timeAt(qfRit1, 1) } },
    { num: 3, home: '1°B', away: '2ª miglior 3ª', andata: { date: dateOf(qfAnd2), time: timeAt(qfAnd2, 0) }, ritorno: { date: dateOf(qfRit2), time: timeAt(qfRit2, 0) } },
    { num: 4, home: '2°B', away: '2°C', andata: { date: dateOf(qfAnd2), time: timeAt(qfAnd2, 1) }, ritorno: { date: dateOf(qfRit2), time: timeAt(qfRit2, 1) } },
  ];

  return (
    <div className="space-y-8" data-testid="knockout-placeholders">
      <div>
        <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-primary" />QUARTI DI FINALE
          <span className="text-xs font-normal text-slate-500 ml-2">(programmati)</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {quarters.map(qf => (
            <Card key={qf.num} className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-sm text-slate-400">
                  Quarto #{qf.num} · <span className="text-primary/80">{qf.home} vs {qf.away}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <PlaceholderLeg legLabel="ANDATA" homeHint={qf.home} awayHint={qf.away} date={qf.andata.date} time={qf.andata.time} />
                <PlaceholderLeg legLabel="RITORNO" homeHint={qf.away} awayHint={qf.home} date={qf.ritorno.date} time={qf.ritorno.time} />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-gold" />SEMIFINALI
          <span className="text-xs font-normal text-slate-500 ml-2">(programmate)</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {[1, 2].map(n => (
            <Card key={n} className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-sm text-slate-400">Semifinale #{n}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <PlaceholderLeg
                  legLabel="ANDATA"
                  homeHint={`Vincente QF${n === 1 ? 1 : 3}`}
                  awayHint={`Vincente QF${n === 1 ? 2 : 4}`}
                  date={dateOf(sfAnd)}
                  time={timeAt(sfAnd, n - 1)}
                />
                <PlaceholderLeg
                  legLabel="RITORNO"
                  homeHint={`Vincente QF${n === 1 ? 2 : 4}`}
                  awayHint={`Vincente QF${n === 1 ? 1 : 3}`}
                  date={dateOf(sfRit)}
                  time={timeAt(sfRit, n - 1)}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-400" />FINALI
          <span className="text-xs font-normal text-slate-500 ml-2">(programmate)</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-sm text-slate-400">Finale 3 / 4 Posto</CardTitle>
            </CardHeader>
            <CardContent>
              <PlaceholderLeg legLabel="" homeHint="Perdente SF1" awayHint="Perdente SF2" date={dateOf(finale)} time={timeAt(finale, 0)} />
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-gold/50 border-2">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-sm text-gold">FINALE 1 / 2 Posto</CardTitle>
            </CardHeader>
            <CardContent>
              <PlaceholderLeg legLabel="" homeHint="Vincente SF1" awayHint="Vincente SF2" date={dateOf(finale)} time={timeAt(finale, 1)} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Scorer entry row
const ScorerRow = ({ scorer, players, onChange, onRemove }) => (
  <div className="flex items-center gap-2">
    <Select value={scorer.player_id} onValueChange={(v) => onChange({ ...scorer, player_id: v })}>
      <SelectTrigger className="flex-1 h-9 text-xs bg-slate-950 border-slate-800">
        <SelectValue placeholder="Seleziona giocatore" />
      </SelectTrigger>
      <SelectContent>
        {players.map(p => (
          <SelectItem key={p.id} value={p.id}>
            {p.first_name} {p.last_name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    <div className="flex items-center gap-1">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={() => onChange({ ...scorer, goals: Math.max(1, (scorer.goals || 1) - 1) })}
      >
        <Minus className="w-3 h-3" />
      </Button>
      <span className="font-numbers text-lg font-bold w-6 text-center">{scorer.goals || 1}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={() => onChange({ ...scorer, goals: (scorer.goals || 1) + 1 })}
      >
        <Plus className="w-3 h-3" />
      </Button>
    </div>
    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={onRemove}>
      <X className="w-3 h-3" />
    </Button>
  </div>
);

export const AdminEliminatoriePage = () => {
  const [knockout, setKnockout] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [scores, setScores] = useState({ home: 0, away: 0 });
  const [saving, setSaving] = useState(false);
  const [allMatches, setAllMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const autoGenRef = useRef(false);

  // Goal scorers state
  const [homePlayers, setHomePlayers] = useState([]);
  const [awayPlayers, setAwayPlayers] = useState([]);
  const [homeScorers, setHomeScorers] = useState([]);
  const [awayScorers, setAwayScorers] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [knockoutRes, settingsRes, matchesRes, teamsRes] = await Promise.all([
        getKnockout(),
        getSettings(),
        getMatches(),
        getTeams()
      ]);
      let ko = knockoutRes.data;
      const groupMatches = (matchesRes.data || []).filter(m => m.phase === 'group');
      const groupStageDone = groupMatches.length > 0 && groupMatches.every(m => m.is_played);
      const hasKO = ko && (ko.quarterfinals?.length > 0 || ko.semifinals?.length > 0 || ko.final_3_4 || ko.final_1_2);

      // Auto-generate knockout if group stage is complete and no knockout yet
      if (!hasKO && groupStageDone && !autoGenRef.current) {
        autoGenRef.current = true;
        try {
          await generateKnockout();
          const [koRes2, matchesRes2] = await Promise.all([getKnockout(), getMatches()]);
          ko = koRes2.data;
          setAllMatches(matchesRes2.data || []);
        } catch (e) {
          autoGenRef.current = false;
        }
      }

      setKnockout(ko);
      setSettings(settingsRes.data);
      if (!autoGenRef.current || !hasKO) setAllMatches(matchesRes.data || []);
      setTeams(teamsRes.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEditMatch = async (match) => {
    setEditingMatch(match);
    setScores({ home: match.home_score || 0, away: match.away_score || 0 });

    // Load players for both teams
    try {
      const [allPlayersRes, goalsRes] = await Promise.all([
        getPlayers(),
        getMatchGoals(match.id)
      ]);
      const all = allPlayersRes.data;
      setHomePlayers(all.filter(p => p.team_id === match.home_team_id && p.role !== 'staff'));
      setAwayPlayers(all.filter(p => p.team_id === match.away_team_id && p.role !== 'staff'));

      // Load existing scorers if match was already played
      const existingGoals = goalsRes.data || [];
      const hScorers = existingGoals
        .filter(g => g.team_id === match.home_team_id)
        .map(g => ({ player_id: g.player_id, goals: g.goals }));
      const aScorers = existingGoals
        .filter(g => g.team_id === match.away_team_id)
        .map(g => ({ player_id: g.player_id, goals: g.goals }));

      setHomeScorers(hScorers.length > 0 ? hScorers : []);
      setAwayScorers(aScorers.length > 0 ? aScorers : []);
    } catch (e) {
      setHomePlayers([]);
      setAwayPlayers([]);
      setHomeScorers([]);
      setAwayScorers([]);
    }
  };

  const handleSaveResult = async () => {
    if (!editingMatch) return;

    // Validate: total scorer goals should match team score
    const homeScorerTotal = homeScorers.reduce((s, sc) => s + (sc.goals || 0), 0);
    const awayScorerTotal = awayScorers.reduce((s, sc) => s + (sc.goals || 0), 0);
    const homeScore = parseInt(scores.home);
    const awayScore = parseInt(scores.away);

    if (homeScore > 0 && homeScorerTotal !== homeScore) {
      toast.error(`Gol marcatori casa (${homeScorerTotal}) non corrispondono al risultato (${homeScore})`);
      return;
    }
    if (awayScore > 0 && awayScorerTotal !== awayScore) {
      toast.error(`Gol marcatori trasferta (${awayScorerTotal}) non corrispondono al risultato (${awayScore})`);
      return;
    }

    // Check all scorers have a player selected
    if (homeScorers.some(s => !s.player_id) || awayScorers.some(s => !s.player_id)) {
      toast.error('Seleziona un giocatore per ogni riga marcatore');
      return;
    }

    setSaving(true);
    try {
      await updateMatch(editingMatch.id, {
        home_score: homeScore,
        away_score: awayScore,
        is_played: true,
        home_scorers: homeScorers,
        away_scorers: awayScorers
      });
      toast.success('Risultato e marcatori salvati');
      setEditingMatch(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleAdvanceKnockout = async () => {
    setAdvancing(true);
    try {
      const response = await advanceKnockout();
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'avanzamento");
    } finally {
      setAdvancing(false);
    }
  };

  const handleRealignDates = async () => {
    if (!window.confirm('Riallineare le date dei match knockout esistenti alle date in Impostazioni? I risultati e marcatori non verranno toccati.')) return;
    try {
      const response = await realignKnockoutDates();
      toast.success(response.data.message || 'Date riallineate');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel riallineamento date');
    }
  };

  const handleDownloadMatchReport = async (match) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiUrl}/api/report/match/${match.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Errore nel download');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const phaseMap = { quarterfinal: 'QF', semifinal: 'SF', final_3_4: 'Finale_3_4', final_1_2: 'Finale_1_2' };
      const prefix = phaseMap[match.phase] || 'match';
      const numSuffix = match.match_number ? `_n${match.match_number}` : '';
      const legSuffix = match.leg ? `_L${match.leg}` : '';
      a.download = `report_${prefix}${numSuffix}${legSuffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report scaricato');
    } catch (error) {
      toast.error('Errore nel download del report');
    }
  };

  const printKnockoutPdf = () => {
    // Lista delle partite eliminatorie ordinate per data
    const all = (allMatches || [])
      .filter(m => ['quarterfinal', 'semifinal', 'final_3_4', 'final_1_2'].includes(m.phase))
      .sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
    const teamById = Object.fromEntries(teams.map(t => [t.id, t]));
    const phaseLabel = (m) => {
      if (m.phase === 'quarterfinal') return `QUARTO${m.leg ? ` · ${m.leg === 1 ? 'ANDATA' : 'RITORNO'}` : ''}`;
      if (m.phase === 'semifinal') return `SEMIFINALE${m.leg ? ` · ${m.leg === 1 ? 'ANDATA' : 'RITORNO'}` : ''}`;
      if (m.phase === 'final_3_4') return 'FINALE 3°/4° POSTO';
      if (m.phase === 'final_1_2') return 'FINALE 1°/2° POSTO';
      return '';
    };

    // Se non ci sono ancora i match (auto-gen non eseguito), usa il piano statico da match_schedule
    const schedule = settings?.match_schedule || [];
    const findEntry = (re) => schedule.find(s => re.test((s.label || '').toLowerCase()));
    const dateOf = (e) => e ? e.date : null;
    const timeAt = (e, idx) => (e && Array.isArray(e.times)) ? e.times[idx] : '';
    const qfAnd1 = findEntry(/^1.*quarto.*andata/);
    const qfRit1 = findEntry(/^1.*quarto.*ritorno/);
    const qfAnd2 = findEntry(/^2.*quarto.*andata/);
    const qfRit2 = findEntry(/^2.*quarto.*ritorno/);
    const sfAnd = findEntry(/semifinal.*andata/);
    const sfRit = findEntry(/semifinal.*ritorno/);
    const finaleE = findEntry(/^finale$/);

    const placeholderRows = !all.length ? [
      { date: dateOf(qfAnd1), time: timeAt(qfAnd1, 0), label: 'QUARTO #1 · ANDATA', home: '1°C', away: '2°A' },
      { date: dateOf(qfAnd1), time: timeAt(qfAnd1, 1), label: 'QUARTO #2 · ANDATA', home: '1°A', away: '1ª miglior 3ª' },
      { date: dateOf(qfRit1), time: timeAt(qfRit1, 0), label: 'QUARTO #1 · RITORNO', home: '2°A', away: '1°C' },
      { date: dateOf(qfRit1), time: timeAt(qfRit1, 1), label: 'QUARTO #2 · RITORNO', home: '1ª miglior 3ª', away: '1°A' },
      { date: dateOf(qfAnd2), time: timeAt(qfAnd2, 0), label: 'QUARTO #3 · ANDATA', home: '1°B', away: '2ª miglior 3ª' },
      { date: dateOf(qfAnd2), time: timeAt(qfAnd2, 1), label: 'QUARTO #4 · ANDATA', home: '2°B', away: '2°C' },
      { date: dateOf(qfRit2), time: timeAt(qfRit2, 0), label: 'QUARTO #3 · RITORNO', home: '2ª miglior 3ª', away: '1°B' },
      { date: dateOf(qfRit2), time: timeAt(qfRit2, 1), label: 'QUARTO #4 · RITORNO', home: '2°C', away: '2°B' },
      { date: dateOf(sfAnd), time: timeAt(sfAnd, 0), label: 'SEMIFINALE #1 · ANDATA', home: 'Vincente QF1', away: 'Vincente QF2' },
      { date: dateOf(sfAnd), time: timeAt(sfAnd, 1), label: 'SEMIFINALE #2 · ANDATA', home: 'Vincente QF3', away: 'Vincente QF4' },
      { date: dateOf(sfRit), time: timeAt(sfRit, 0), label: 'SEMIFINALE #1 · RITORNO', home: 'Vincente QF2', away: 'Vincente QF1' },
      { date: dateOf(sfRit), time: timeAt(sfRit, 1), label: 'SEMIFINALE #2 · RITORNO', home: 'Vincente QF4', away: 'Vincente QF3' },
      { date: dateOf(finaleE), time: timeAt(finaleE, 0), label: 'FINALE 3°/4° POSTO', home: 'Perdente SF1', away: 'Perdente SF2' },
      { date: dateOf(finaleE), time: timeAt(finaleE, 1), label: 'FINALE 1°/2° POSTO', home: 'Vincente SF1', away: 'Vincente SF2' },
    ] : [];

    const rows = all.length
      ? all.map(m => {
          const home = teamById[m.home_team_id]?.name || 'TBD';
          const away = teamById[m.away_team_id]?.name || 'TBD';
          const time = new Date(m.match_date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
          const dateKey = m.match_date?.substring(0, 10);
          const score = m.is_played ? `${m.home_score} - ${m.away_score}` : '-';
          return { dateKey, time, label: phaseLabel(m), home, away, score, played: m.is_played };
        })
      : placeholderRows.map(r => ({ dateKey: r.date, time: r.time || '', label: r.label, home: r.home, away: r.away, score: '-', played: false }));

    // raggruppa per data
    const byDate = {};
    rows.forEach(r => {
      const k = r.dateKey || 'TBD';
      if (!byDate[k]) byDate[k] = [];
      byDate[k].push(r);
    });

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html><head><title>Calendario Fasi Eliminatorie</title>
      <style>
        @page { size: A4 portrait; margin: 8mm; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; }
        body { font-family: Arial, Helvetica, sans-serif; padding: 4mm 6mm; color: #111; font-size: 10pt; }
        h1 { text-align: center; margin: 0 0 2mm; font-size: 16pt; }
        h2 { text-align: center; color: #666; margin: 0 0 4mm; font-weight: normal; font-size: 10pt; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 4px 6px; border: 1px solid #d0d0d0; line-height: 1.2; }
        th { background: #f0f0f0; font-size: 9pt; text-transform: uppercase; letter-spacing: 0.3px; }
        .date-row td { background: #1a1a2e; color: #fff; font-weight: bold; padding: 5px 6px; font-size: 10pt; }
        .label { color: #2a7a2a; font-weight: bold; font-size: 9pt; }
        .vs { text-align: center; color: #888; width: 24px; }
        .score { text-align: center; font-weight: bold; width: 70px; }
        .time { text-align: center; color: #555; width: 50px; font-variant-numeric: tabular-nums; }
        .home, .away { width: auto; }
        .home { text-align: right; }
        .played { background: #f4fff4; }
        .footer { text-align: center; margin-top: 4mm; color: #999; font-size: 8pt; }
      </style></head>
      <body>
        <h1>TORNEO NOTTURNO DI CALCIO</h1>
        <h2>30ª Edizione - Viadana Bresciana - Calendario Fasi Eliminatorie</h2>
        <table>
          <thead><tr>
            <th class="time">Ora</th>
            <th class="label">Fase</th>
            <th class="home">Squadra Casa</th>
            <th class="vs"></th>
            <th>Squadra Ospite</th>
            <th class="score">Risultato</th>
          </tr></thead>
          <tbody>
            ${Object.keys(byDate).sort().map(dateKey => {
              const dateFormatted = dateKey !== 'TBD'
                ? new Date(dateKey + 'T12:00:00Z').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
                : 'Data da definire';
              return `
                <tr class="date-row"><td colspan="6">${dateFormatted}</td></tr>
                ${byDate[dateKey].map(r => `
                  <tr class="${r.played ? 'played' : ''}">
                    <td class="time">${r.time}</td>
                    <td class="label">${r.label}</td>
                    <td class="home">${r.home}</td>
                    <td class="vs">vs</td>
                    <td>${r.away}</td>
                    <td class="score">${r.score}</td>
                  </tr>
                `).join('')}
              `;
            }).join('')}
          </tbody>
        </table>
        <p class="footer">Generato il ${new Date().toLocaleDateString('it-IT')} - Torneo Notturno Viadana Bresciana</p>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  const groupByMatchNumber = (matches) => {
    const grouped = {};
    for (const match of matches) {
      const num = match.match_number || 0;
      if (!grouped[num]) grouped[num] = { leg1: null, leg2: null };
      if (match.leg === 1) grouped[num].leg1 = match;
      else if (match.leg === 2) grouped[num].leg2 = match;
    }
    return grouped;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const hasKnockout = knockout && (
    knockout.quarterfinals?.length > 0 ||
    knockout.semifinals?.length > 0 ||
    knockout.final_3_4 ||
    knockout.final_1_2
  );

  const qfGroups = groupByMatchNumber(knockout?.quarterfinals || []);
  const sfGroups = groupByMatchNumber(knockout?.semifinals || []);

  const allQfPlayed = knockout?.quarterfinals?.length === 8 &&
    knockout.quarterfinals.every(m => m.is_played);
  const allSfPlayed = knockout?.semifinals?.length === 4 &&
    knockout.semifinals.every(m => m.is_played);
  const canAdvance = (allQfPlayed && knockout?.semifinals?.length === 0) ||
    (allSfPlayed && !knockout?.final_1_2);

  return (
    <div className="p-4 md:p-6" data-testid="admin-eliminatorie">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold mb-1">FASI ELIMINATORIE</h1>
            <p className="text-slate-400 text-sm">Quarti, semifinali e finali con andata e ritorno</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={handleRealignDates}
              variant="outline"
              className="border-slate-700 hover:border-primary"
              data-testid="realign-knockout-dates-btn"
              title="Aggiorna le date dei match knockout esistenti usando le date in Impostazioni"
            >
              <CalendarClock className="w-4 h-4 mr-2" />
              Riallinea Date
            </Button>
            <Button
              onClick={printKnockoutPdf}
              variant="outline"
              className="border-slate-700 hover:border-primary"
              data-testid="knockout-pdf-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Stampa Calendario PDF
            </Button>
          </div>
        </div>

        {/* Placeholders schedule (visible BEFORE the knockout is generated) */}
        {!hasKnockout && <KnockoutPlaceholders settings={settings} />}

        {/* Advance Button */}
        {hasKnockout && canAdvance && (
          <Card className="bg-slate-900 border-gold/30 border-2 mb-8">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-heading text-lg font-bold text-gold">Avanza alla fase successiva</h3>
                  <p className="text-sm text-slate-400">{allQfPlayed && !allSfPlayed ? 'Genera le semifinali' : 'Genera le finali'}</p>
                </div>
                <Button onClick={handleAdvanceKnockout} disabled={advancing} className="bg-gold text-slate-950 hover:bg-gold/90" data-testid="advance-knockout-btn">
                  {advancing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <ChevronRight className="w-5 h-5 mr-2" />}
                  Avanza
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Knockout Bracket */}
        {hasKnockout && (
          <div className="space-y-8">
            {Object.keys(qfGroups).length > 0 && (
              <div>
                <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-primary" />QUARTI DI FINALE
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.entries(qfGroups).sort(([a], [b]) => Number(a) - Number(b)).map(([num, { leg1, leg2 }]) => (
                    <Card key={num} className="bg-slate-900 border-slate-800">
                      <CardHeader className="pb-2"><CardTitle className="font-heading text-sm text-slate-400">Quarto #{num}</CardTitle></CardHeader>
                      <CardContent className="space-y-2">
                        <MatchCard match={leg1} onEdit={handleEditMatch} onDownloadReport={handleDownloadMatchReport} />
                        <MatchCard match={leg2} onEdit={handleEditMatch} onDownloadReport={handleDownloadMatchReport} />
                        <TieDisplay leg1={leg1} leg2={leg2} />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(sfGroups).length > 0 && (
              <div>
                <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-gold" />SEMIFINALI
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.entries(sfGroups).sort(([a], [b]) => Number(a) - Number(b)).map(([num, { leg1, leg2 }]) => (
                    <Card key={num} className="bg-slate-900 border-slate-800">
                      <CardHeader className="pb-2"><CardTitle className="font-heading text-sm text-slate-400">Semifinale #{num}</CardTitle></CardHeader>
                      <CardContent className="space-y-2">
                        <MatchCard match={leg1} onEdit={handleEditMatch} onDownloadReport={handleDownloadMatchReport} />
                        <MatchCard match={leg2} onEdit={handleEditMatch} onDownloadReport={handleDownloadMatchReport} />
                        <TieDisplay leg1={leg1} leg2={leg2} />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {(knockout?.final_3_4 || knockout?.final_1_2) && (
              <div>
                <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-400" />FINALI
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {knockout?.final_3_4 && (
                    <Card className="bg-slate-900 border-slate-800">
                      <CardHeader className="pb-2"><CardTitle className="font-heading text-sm text-slate-400">Finale 3 / 4 Posto</CardTitle></CardHeader>
                      <CardContent><MatchCard match={knockout.final_3_4} onEdit={handleEditMatch} onDownloadReport={handleDownloadMatchReport} showLeg={false} /></CardContent>
                    </Card>
                  )}
                  {knockout?.final_1_2 && (
                    <Card className="bg-slate-900 border-gold/50 border-2">
                      <CardHeader className="pb-2"><CardTitle className="font-heading text-sm text-gold">FINALE 1 / 2 Posto</CardTitle></CardHeader>
                      <CardContent><MatchCard match={knockout.final_1_2} onEdit={handleEditMatch} onDownloadReport={handleDownloadMatchReport} showLeg={false} /></CardContent>
                    </Card>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Edit Result + Scorers Dialog */}
        <Dialog open={!!editingMatch} onOpenChange={() => setEditingMatch(null)}>
          <DialogContent className="bg-slate-900 border-slate-800 mx-4 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Inserisci Risultato e Marcatori</DialogTitle>
            </DialogHeader>

            {editingMatch && (
              <div className="space-y-6">
                {/* Score inputs */}
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center flex-1">
                    <p className="font-heading font-bold mb-2 text-sm truncate">{editingMatch.home_team?.name || 'TBD'}</p>
                    <Input
                      type="number" min="0" value={scores.home}
                      onChange={(e) => setScores({ ...scores, home: e.target.value })}
                      className="text-center text-2xl font-numbers h-14 bg-slate-950 border-slate-800"
                      data-testid="knockout-home-score"
                    />
                  </div>
                  <span className="text-xl text-slate-600 mt-6">-</span>
                  <div className="text-center flex-1">
                    <p className="font-heading font-bold mb-2 text-sm truncate">{editingMatch.away_team?.name || 'TBD'}</p>
                    <Input
                      type="number" min="0" value={scores.away}
                      onChange={(e) => setScores({ ...scores, away: e.target.value })}
                      className="text-center text-2xl font-numbers h-14 bg-slate-950 border-slate-800"
                      data-testid="knockout-away-score"
                    />
                  </div>
                </div>

                {/* Home Scorers */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-300 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" />
                      Marcatori {editingMatch.home_team?.name || 'Casa'}
                    </h4>
                    <Button
                      type="button" variant="ghost" size="sm"
                      className="h-7 text-xs text-primary"
                      onClick={() => setHomeScorers([...homeScorers, { player_id: '', goals: 1 }])}
                      data-testid="add-home-scorer-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Aggiungi
                    </Button>
                  </div>
                  {homeScorers.length > 0 ? (
                    <div className="space-y-2">
                      {homeScorers.map((scorer, idx) => (
                        <ScorerRow
                          key={idx}
                          scorer={scorer}
                          players={homePlayers}
                          onChange={(updated) => {
                            const copy = [...homeScorers];
                            copy[idx] = updated;
                            setHomeScorers(copy);
                          }}
                          onRemove={() => setHomeScorers(homeScorers.filter((_, i) => i !== idx))}
                        />
                      ))}
                      <p className="text-xs text-slate-500 text-right">
                        Totale: {homeScorers.reduce((s, sc) => s + (sc.goals || 0), 0)} gol
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">Nessun marcatore aggiunto</p>
                  )}
                </div>

                {/* Away Scorers */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-300 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" />
                      Marcatori {editingMatch.away_team?.name || 'Trasferta'}
                    </h4>
                    <Button
                      type="button" variant="ghost" size="sm"
                      className="h-7 text-xs text-primary"
                      onClick={() => setAwayScorers([...awayScorers, { player_id: '', goals: 1 }])}
                      data-testid="add-away-scorer-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Aggiungi
                    </Button>
                  </div>
                  {awayScorers.length > 0 ? (
                    <div className="space-y-2">
                      {awayScorers.map((scorer, idx) => (
                        <ScorerRow
                          key={idx}
                          scorer={scorer}
                          players={awayPlayers}
                          onChange={(updated) => {
                            const copy = [...awayScorers];
                            copy[idx] = updated;
                            setAwayScorers(copy);
                          }}
                          onRemove={() => setAwayScorers(awayScorers.filter((_, i) => i !== idx))}
                        />
                      ))}
                      <p className="text-xs text-slate-500 text-right">
                        Totale: {awayScorers.reduce((s, sc) => s + (sc.goals || 0), 0)} gol
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">Nessun marcatore aggiunto</p>
                  )}
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setEditingMatch(null)} className="border-slate-700 flex-1 sm:flex-none">
                Annulla
              </Button>
              <Button
                onClick={handleSaveResult} disabled={saving}
                className="bg-primary text-slate-950 flex-1 sm:flex-none"
                data-testid="save-knockout-result-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva Risultato'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminEliminatoriePage;
